import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type DocumentData,
} from 'firebase/firestore'
import { getFirebaseApp } from '../lib/firebase'
import type { InquiryInput, InquiryStatus, WebsiteInquiry } from '../types/inquiry'

type TimestampLike = { toDate: () => Date }

function toISOString(value: unknown): string | null {
  if (
    value &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof (value as TimestampLike).toDate === 'function'
  ) {
    return (value as TimestampLike).toDate().toISOString()
  }
  return null
}

function inquiryFromData(id: string, data: DocumentData): WebsiteInquiry {
  const status: InquiryStatus = data.status === 'in_progress' || data.status === 'closed'
    ? data.status
    : 'new'

  return {
    id,
    name: String(data.name ?? ''),
    email: String(data.email ?? ''),
    company: String(data.company ?? ''),
    website: String(data.website ?? ''),
    message: String(data.message ?? ''),
    sourcePath: String(data.sourcePath ?? '/'),
    status,
    internalNotes: String(data.internalNotes ?? ''),
    createdAt: toISOString(data.createdAt),
    updatedAt: toISOString(data.updatedAt),
  }
}

export async function submitWebsiteInquiry(input: InquiryInput): Promise<string> {
  const inquiry = await addDoc(collection(getFirestore(getFirebaseApp()), 'websiteInquiries'), {
    name: input.name.trim(),
    email: input.email.trim().toLowerCase(),
    company: input.company.trim(),
    website: input.website.trim(),
    message: input.message.trim(),
    sourcePath: input.sourcePath.slice(0, 500),
    status: 'new',
    internalNotes: '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })

  return inquiry.id
}

export async function listWebsiteInquiries(): Promise<WebsiteInquiry[]> {
  const snapshot = await getDocs(
    query(
      collection(getFirestore(getFirebaseApp()), 'websiteInquiries'),
      orderBy('createdAt', 'desc'),
      limit(200),
    ),
  )
  return snapshot.docs.map(item => inquiryFromData(item.id, item.data()))
}

export function subscribeToNewWebsiteInquiries(
  onInquiry: (inquiry: WebsiteInquiry) => void,
  onError: (error: Error) => void,
): () => void {
  const listeningSince = Date.now()
  let initialized = false

  return onSnapshot(
    query(
      collection(getFirestore(getFirebaseApp()), 'websiteInquiries'),
      orderBy('createdAt', 'desc'),
      limit(200),
    ),
    snapshot => {
      if (!initialized) {
        initialized = true
        return
      }

      snapshot.docChanges().forEach(change => {
        if (change.type !== 'added') return
        const inquiry = inquiryFromData(change.doc.id, change.doc.data())
        const receivedAt = inquiry.createdAt ? Date.parse(inquiry.createdAt) : Number.NaN
        if (Number.isFinite(receivedAt) && receivedAt < listeningSince - 30_000) return
        onInquiry(inquiry)
      })
    },
    onError,
  )
}

export async function updateWebsiteInquiry(
  id: string,
  status: InquiryStatus,
  internalNotes: string,
): Promise<void> {
  await updateDoc(doc(getFirestore(getFirebaseApp()), 'websiteInquiries', id), {
    status,
    internalNotes: internalNotes.trim(),
    updatedAt: serverTimestamp(),
  })
}

export async function deleteWebsiteInquiry(id: string): Promise<void> {
  await deleteDoc(doc(getFirestore(getFirebaseApp()), 'websiteInquiries', id))
}
